import { useState, useEffect } from 'react';
import { Loader2, ArrowLeft, Archive, Calendar } from 'lucide-react';
import AdminShell from '../components/AdminShell';
import Toast from '../components/Toast';
import WeeklyCardBack from '../components/WeeklyCardBack';
import { useApp } from '../lib/AppContext';
import { getWeekWithCards, archiveWeek, WeekWithCards } from '../lib/weeklyCardsPlanner';

function formatWeekLabel(weekOf: string): string {
  const date = new Date(weekOf + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminWeeklyCardsViewPublished() {
  const { navigateTo, navigationState } = useApp();
  const weekId = navigationState?.weekId as string;

  const [loading, setLoading] = useState(true);
  const [edition, setEdition] = useState<WeekWithCards | null>(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  useEffect(() => {
    if (!weekId) {
      navigateTo('admin-weekly-cards');
      return;
    }
    loadEdition();
  }, [weekId]);

  async function loadEdition() {
    if (!weekId) return;

    setLoading(true);
    const data = await getWeekWithCards(weekId);

    if (!data) {
      showToast('Edition not found', 'error');
      setTimeout(() => navigateTo('admin-weekly-cards'), 2000);
      return;
    }

    setEdition(data);
    setLoading(false);
  }

  async function handleArchive() {
    if (!edition || archiving) return;

    setArchiving(true);
    try {
      const success = await archiveWeek(edition.week.id);
      if (success) {
        showToast('Archived successfully', 'success');
        setTimeout(() => navigateTo('admin-weekly-cards'), 1500);
      } else {
        showToast('Failed to archive', 'error');
        setArchiving(false);
      }
    } catch (err) {
      console.error('[AdminWeeklyCardsViewPublished] Error archiving:', err);
      showToast('Failed to archive', 'error');
      setArchiving(false);
    }
  }

  function showToast(message: string, type: 'success' | 'error') {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  }

  if (loading) {
    return (
      <AdminShell>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#40E0D0] animate-spin" />
        </div>
      </AdminShell>
    );
  }

  if (!edition) {
    return null;
  }

  const artistWisdom = edition.cards.find(c => c.card_type === 'artist_wisdom');
  const civicMoment = edition.cards.find(c => c.card_type === 'civic_moment');
  const sparkThought = edition.cards.find(c => c.card_type === 'spark_a_thought');

  return (
    <AdminShell>
      <div className="p-8 w-full max-w-[1120px]">
        <div className="mb-8">
          <button
            onClick={() => navigateTo('admin-weekly-cards')}
            className="flex items-center gap-2 text-[14px] text-[#808080] hover:text-[#F5F5F5] transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Weekly Cards
          </button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-[28px] font-semibold text-[#F5F5F5]">Published</h1>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wide bg-[#40E0D0]/10 text-[#40E0D0]">
                  {edition.week.status}
                </span>
              </div>
              <p className="text-[14px] text-[#808080] mb-1">
                Week of {formatWeekLabel(edition.week.week_of)}
              </p>
              <div className="flex items-center gap-4 text-[12px] text-[#606060]">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  Published {edition.week.published_at
                    ? new Date(edition.week.published_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                    : '—'}
                </div>
                <div>
                  Last updated {new Date(edition.week.updated_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
                <div>
                  Cards configured: {edition.cards.length} / 3
                </div>
              </div>
            </div>

            {edition.week.status === 'published' && (
              <button
                onClick={() => setShowArchiveModal(true)}
                className="px-5 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors flex items-center gap-2"
              >
                <Archive className="w-4 h-4" />
                Archive
              </button>
            )}
          </div>
        </div>

        <div className="bg-[#141414] border border-[#282828] rounded-xl p-8">
          <div className="mb-4">
            <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-1">Card Preview</h3>
            <p className="text-[12px] text-[#606060]">Read-only view of published edition</p>
          </div>

          <div className="flex flex-row items-center justify-center gap-6">
            <div
              className="origin-top-left"
              style={{
                width: '300px',
                height: '400px',
                transform: 'scale(0.833)',
              }}
            >
              <div className="w-[360px] h-[480px] bg-bg-card rounded-[20px] border border-stroke flex flex-col"
                style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}
              >
                {artistWisdom?.content ? (
                  <WeeklyCardBack
                    cardType="artist_wisdom"
                    imageUrl={artistWisdom.image_url}
                    content={artistWisdom.content}
                    attribution={artistWisdom.attribution}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-[13px] text-[#606060] text-center">Not set</p>
                  </div>
                )}
              </div>
            </div>

            <div
              className="origin-top-left"
              style={{
                width: '300px',
                height: '400px',
                transform: 'scale(0.833)',
              }}
            >
              <div className="w-[360px] h-[480px] bg-bg-card rounded-[20px] border border-stroke flex flex-col"
                style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}
              >
                {civicMoment?.content ? (
                  <WeeklyCardBack
                    cardType="civic_moment"
                    imageUrl={civicMoment.country?.image_url}
                    content={civicMoment.content}
                    title={civicMoment.title}
                    countryName={civicMoment.country?.name}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-[13px] text-[#606060] text-center">Not set</p>
                  </div>
                )}
              </div>
            </div>

            <div
              className="origin-top-left"
              style={{
                width: '300px',
                height: '400px',
                transform: 'scale(0.833)',
              }}
            >
              <div className="w-[360px] h-[480px] bg-bg-card rounded-[20px] border border-stroke flex flex-col"
                style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}
              >
                {sparkThought?.content ? (
                  <WeeklyCardBack
                    cardType="civic_spark"
                    imageUrl={null}
                    content={sparkThought.content}
                    subtext={sparkThought.subtext}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-[13px] text-[#606060] text-center">Not set</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-[20px] font-semibold text-[#F5F5F5] mb-3">Archive this edition?</h2>
            <p className="text-[14px] text-[#A0A0A0] mb-6 leading-relaxed">
              This will hide it from Published History and move it to Archived. You can restore it later.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowArchiveModal(false)}
                disabled={archiving}
                className="px-5 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleArchive}
                disabled={archiving}
                className="px-5 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {archiving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Archiving...
                  </>
                ) : (
                  'Archive'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}
    </AdminShell>
  );
}
